/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GslbApiService, GslbRealServiceModel, GslbTopologyModel, LbErrorService } from '../../../../shared';
import { IPModel } from '../../../../shared/models/ip.model';
import { LoadingService, LogService, NavigationService, UtilsService } from '../../../../../../core/services';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { ipValidator } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-topology',
    templateUrl: './addEditTopology.component.html'
})

export class AddEditTopologyComponent extends BaseComponent {
    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        membersWeight: [],
        backEndMonitor: [],
        backEndMonitorRetries: [],
        backendMonitorStatus: [],
        backendLbMethod: [],
        backendFallback: [],
        members: []
    };

    /**
     * Store the id of the topology to edit.
     */
    public editId: string;

    /**
     * Topology from group.
     */
    public topologyForm: FormGroup;

    /**
     * Our available made topologies from the API.
     */
    public topologies: GslbTopologyModel[] = [];

    /**
     * Our available made members from the API.
     */
    public members: GslbRealServiceModel[] = [];

    /**
     * To store the edit topology.
     * @private
     */
    private currentTopology: any;

    /**
     * Store initial data of the form to compare with any changes.
     * @private
     */
    private initialData: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private gslbApiService: GslbApiService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private navigationService: NavigationService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpForm();
        this.setUpFormSubscribers();
        this.setUpPageData();
    }

    /**
     * Save the topology.
     */
    public submitTopology(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiCall = this.getApiCall();

            apiCall.pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.navigateToURL('/cluster-configuration/gslb?sFilter=topologies');
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('GslbComponent: submitTopology', 'Error submitting topology', error);
                        this.lbErrorService.pageWithErrors(this.topologyForm, error.error.errors);
                        this.loadingService.loadingButtonEmitter.next(false);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get topology data for submitting.
     * @private
     */
    private getApiCall(): any {
        const topology: GslbTopologyModel = <GslbTopologyModel>{};
        topology.subNet = <IPModel>{};
        topology.subNet.ip = this.topologyForm.get('ip').value;
        topology.subNet.mask = this.topologyForm.get('mask').value;
        topology.member = this.getMemberId();

        const apiCall = this.editId
            ? this.gslbApiService.editTopology(topology)
            : this.gslbApiService.addTopology(topology);

        if (this.editId) {
            topology.id = this.currentTopology.id;
        }

        return apiCall;
    }

    /**
     * Set up the page.
     * @private
     */
    private setUpPageData(): void {
        this.editId = this.route.snapshot.paramMap.get('id');

        forkJoin({
            members: this.gslbApiService.getRealServers(),
            topologies: this.gslbApiService.getTopologies(),
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['members'] && dataGroup['members'].length) {
                        this.members = dataGroup['members'];
                        this.setUpDropdown();
                    }

                    if (dataGroup['topologies'] && dataGroup['topologies'].length) {
                        this.topologies = dataGroup['topologies'];
                        if (this.editId) {
                            this.patchDataToForm();
                        }
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AddEditTopologyComponent: setupPageData()',
                        'Error getting data for page.',
                        error
                    );
                }
            });
    }

    /**
     * Get the selected member Ids to send to the API.
     * @private
     */
    private getMemberId(): string {
        const memberName = this.topologyForm.get('member').value;
        const selectedMember: GslbRealServiceModel = this.members.find(
            (member: GslbRealServiceModel): boolean => member.name === memberName
        );

        return selectedMember.id;
    }

    /**
     * Set up the topology data.
     * @private
     */
    private patchDataToForm(): void {
        this.currentTopology = this.topologies.find((item: GslbTopologyModel): boolean => item.id === this.editId);
        this.topologyForm.get('ip').setValue(this.currentTopology.subNet.ip);
        this.topologyForm.get('mask').setValue(this.currentTopology.subNet.mask);
        const topologyMember: GslbRealServiceModel = this.members.find(
            (item: GslbRealServiceModel): boolean => item.id === this.currentTopology.member
        );
        this.topologyForm.get('member').setValue(topologyMember.name);
        this.initialData = this.topologyForm.value;
    }

    /**
     * Set up Topology form.
     * @private
     */
    private setUpForm(): void {
        this.topologyForm = this.formBuilder.group({
            ip: [null, [Validators.required, ipValidator]],
            mask: [null, [Validators.required, Validators.min(0), Validators.max(128)]],
            member: [null, [Validators.required]]
        });
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.topologyForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    this.utilsService.debounce('formChanges', (): void => {
                        if (this.editId
                            && !this.utilsService.hasFormDataChanged(this.initialData, value)) {
                            this.topologyForm.markAsPristine();
                        } else {
                            this.topologyForm.markAsDirty();
                        }
                    }, 250);
                }
            });
    }

    /**
     * Set up the dropdown values.
     * @private
     */
    private setUpDropdown(): void {
        const members: any[] = [];
        if (this.members) {
            for (const member of this.members) {
                members.push(<DropdownModel>{ key: member.name, value: member.name });
            }
        }
        this.dropdown.members = members;
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }
}
